import { ListingResource, GreenVerificationField } from '../../system/field';
import { BaseResourceManager } from '../BaseResourceManager';
import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { useReso } from '../../contexts/ResoContext';

export class GreenVerificationResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[GreenVerificationField.GREEN_VERIFICATION_KEY];
  }

  getItemNumber(): number | undefined {
    return undefined; // Green verifications don't have item numbers
  }

  getListingKeyField(): string {
    return GreenVerificationField.GREEN_VERIFICATION_LISTING_KEY;
  }

  getListingKey(): string | undefined {
    return this[GreenVerificationField.GREEN_VERIFICATION_LISTING_KEY];
  }

  // Utility methods for green verification resources

  /**
   * Get the verification body
   */
  getBody(): string | null {
    return this[GreenVerificationField.GREEN_VERIFICATION_BODY] || null;
  }

  /**
   * Get the program type
   */
  getProgramType(): string | null {
    return this[GreenVerificationField.GREEN_VERIFICATION_PROGRAM_TYPE] || null;
  }

  /**
   * Get the verification year
   */
  getYear(): number | null {
    const year = this[GreenVerificationField.GREEN_VERIFICATION_YEAR];
    return year ? Number(year) : null;
  }

  /**
   * Get the rating
   */
  getRating(): string | null {
    return this[GreenVerificationField.GREEN_VERIFICATION_RATING] || null;
  }

  /**
   * Get the score
   */
  getScore(): number | null {
    const score = this[GreenVerificationField.GREEN_VERIFICATION_SCORE];
    return score !== undefined && score !== null ? Number(score) : null;
  }

  /**
   * Get the status
   */
  getStatus(): string | null {
    return this[GreenVerificationField.GREEN_VERIFICATION_STATUS] || null;
  }
}

export class GreenVerificationResourceManager extends BaseResourceManager<GreenVerificationResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: {
      enableCwa?: boolean;
      useTranslation?: boolean;
    } = {},
  ) {
    super(
      ListingResource.GreenVerification,
      executeQuery,
      fieldTranslator,
      taskManager,
      options,
    );
  }

  /**
   * Core mapping function from API data to GreenVerificationResource
   */
  protected mapFromApiCore(apiData: any): GreenVerificationResource {
    return new GreenVerificationResource(apiData);
  }

  /**
   * Core mapping function from GreenVerificationResource to API data
   */
  protected mapToApiCore(resource: GreenVerificationResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Filter green verifications by program type
   */
  public async getByProgramType(
    programType: string,
    listingKey?: string,
  ): Promise<GreenVerificationResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getByProgramType' },
      async (taskId) => {
        try {
          // Get all green verifications
          const allVerifications = await this.list(listingKey);

          // Filter by program type
          return allVerifications.filter(
            (verification) =>
              verification[
                GreenVerificationField.GREEN_VERIFICATION_PROGRAM_TYPE
              ] === programType,
          );
        } catch (error) {
          console.error(
            'Error getting green verifications by program type:',
            error,
          );
          return [];
        }
      },
    );
  }

  /**
   * Get green verifications with a minimum score
   */
  public async getByMinimumScore(
    minScore: number,
    listingKey?: string,
  ): Promise<GreenVerificationResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getByMinimumScore' },
      async (taskId) => {
        try {
          // Get all green verifications
          const allVerifications = await this.list(listingKey);

          // Filter by minimum score
          return allVerifications.filter((verification) => {
            const score = verification.getScore();
            return score !== null && score >= minScore;
          });
        } catch (error) {
          console.error(
            'Error getting green verifications by minimum score:',
            error,
          );
          return [];
        }
      },
    );
  }
}

/**
 * Create a new GreenVerificationResource with required fields
 */
export function createGreenVerificationResource(
  data: Partial<Record<string, any>> = {},
): GreenVerificationResource {
  const defaults = {
    [GreenVerificationField.GREEN_VERIFICATION_STATUS]: 'Active',
  };

  return new GreenVerificationResource({
    ...defaults,
    ...data,
  });
}
